import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref, Ref } from 'lit/directives/ref.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import { styles as sharedStyles } from '../styles/shared-styles';
import { ApiFactory } from '../api/easypos-api';
import type { ICreditNote } from '../api/model/requests';
import { creditNoteRedeemable } from '../datatypes/credit-note';
import { money } from '../datatypes/currency';

type SlInputLike = HTMLElement & { value: string };

// A scanned wallet QR encodes the note's ULID id; a manually typed human code
// hits the by-code lookup. So we try id-first when the input is ULID-shaped,
// code-first otherwise, and fall back to the other before giving up.
const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/i;

// P2c - store-credit picker. Lets the cashier apply ONE credit note to the
// current sale: tap one of the attached member's redeemable notes, or resolve a
// bearer note by typed/scanned code. Emits `credit-selected` with the chosen
// note; the till computes how much to apply (min of redeemable + amount due)
// and performs the actual debit server-side at sale creation. Emits
// `credit-cancelled` on dismiss.
@customElement('credit-note-picker')
export class CreditNotePicker extends LitElement {
  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
      }

      sl-dialog::part(panel) {
        max-width: min(420px, 95vw);
      }

      .due-line {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        padding: 0.4rem 0.6rem;
        background: var(--sl-color-neutral-100);
        border-radius: var(--sl-border-radius-medium);
        margin-bottom: 0.8rem;
        font-size: 0.9rem;
      }
      .due-line .label {
        color: var(--sl-color-neutral-700);
      }
      .due-line .amount {
        font-weight: 700;
        font-variant-numeric: tabular-nums;
      }

      .section-label {
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--sl-color-neutral-600);
        margin: 0.4rem 0 0.4rem;
      }

      .notes {
        display: flex;
        flex-direction: column;
        gap: 0.4rem;
        margin-bottom: 0.6rem;
      }

      .note {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 0.5rem;
        width: 100%;
        padding: 0.6rem 0.75rem;
        border: 1px solid var(--sl-color-neutral-200);
        border-radius: var(--sl-border-radius-medium);
        background: var(--sl-color-neutral-0);
        cursor: pointer;
        text-align: left;
        font: inherit;
      }
      .note:hover {
        border-color: var(--sl-color-primary-400);
        background: var(--sl-color-primary-50);
      }
      .note .code {
        font-weight: 600;
        font-variant-numeric: tabular-nums;
      }
      .note .meta {
        font-size: 0.78rem;
        color: var(--sl-color-neutral-600);
      }
      .note .bal {
        font-weight: 700;
        font-variant-numeric: tabular-nums;
        color: var(--sl-color-success-700);
        white-space: nowrap;
      }

      .empty {
        font-size: 0.85rem;
        color: var(--sl-color-neutral-600);
        padding: 0.4rem 0.2rem 0.8rem;
      }

      .divider {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: var(--sl-color-neutral-500);
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin: 0.4rem 0 0.6rem;
      }
      .divider::before,
      .divider::after {
        content: '';
        flex: 1;
        height: 1px;
        background: var(--sl-color-neutral-200);
      }

      .code-row {
        display: flex;
        gap: 0.5rem;
        align-items: flex-end;
      }
      .code-row sl-input {
        flex: 1;
      }

      .footer-buttons {
        display: flex;
        justify-content: flex-end;
        gap: 0.5rem;
      }
    `,
  ];

  // The bill being paid (₱). Used to preview how much of a note will apply.
  @property({ type: Number }) amountDue = 0;

  // The shop the sale belongs to - scopes the credit-note lookups.
  @property() shopName = '';

  // The attached member's redeemable notes, if a member is at the till. May be
  // empty (no member, or no live credit) - the code/QR path still works.
  @property({ attribute: false }) memberNotes: ICreditNote[] = [];

  // Display name of the attached member, for the list heading.
  @property() memberName: string | null = null;

  @property({ type: Boolean, reflect: true }) open = false;

  @state() private _lookupBusy = false;
  @state() private _error: string | null = null;

  private _inputRef: Ref<SlInputLike> = createRef();

  willUpdate(changed: Map<string, unknown>) {
    // Clear any stale error / input each time the dialog is freshly opened.
    if (changed.has('open') && this.open && !changed.get('open')) {
      this._error = null;
      this._lookupBusy = false;
    }
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('open') && this.open && this._inputRef.value) {
      this._inputRef.value.value = '';
    }
  }

  private _select(note: ICreditNote) {
    this.dispatchEvent(
      new CustomEvent<{ note: ICreditNote }>('credit-selected', {
        detail: { note },
        bubbles: true,
        composed: true,
      })
    );
  }

  private _cancel() {
    this.dispatchEvent(
      new CustomEvent('credit-cancelled', { bubbles: true, composed: true })
    );
  }

  // Resolve the typed/scanned code into a note, validate it's spendable, then
  // hand it to the parent. The authoritative debit still happens server-side.
  private async _applyCode() {
    const raw = (this._inputRef.value?.value ?? '').trim();
    if (!raw || this._lookupBusy || !this.shopName) return;
    this._error = null;
    this._lookupBusy = true;
    try {
      const api = ApiFactory.authenticated();
      const isUlid = ULID_RE.test(raw);
      // Try the most likely lookup first, fall back to the other.
      let note: ICreditNote | null = null;
      const first = isUlid
        ? await api.getCreditNote(this.shopName, raw)
        : await api.resolveCreditNoteByCode(this.shopName, raw);
      if (first.success && first.data) {
        note = first.data;
      } else {
        const second = isUlid
          ? await api.resolveCreditNoteByCode(this.shopName, raw)
          : await api.getCreditNote(this.shopName, raw);
        if (second.success && second.data) note = second.data;
      }
      if (!note) {
        this._error = `No store-credit note found for "${raw}".`;
        return;
      }
      if (creditNoteRedeemable(note) <= 0) {
        this._error = `That note can't be used (it's empty, expired, or void).`;
        return;
      }
      this._select(note);
    } finally {
      this._lookupBusy = false;
    }
  }

  render() {
    const now = Date.now();
    const redeemable = this.memberNotes
      .filter((n) => creditNoteRedeemable(n, now) > 0)
      .sort((a, b) => b.remaining - a.remaining);

    return html`
      <sl-dialog
        class="full-page-on-phone"
        label="Apply store credit"
        ?open=${this.open}
        @sl-request-close=${(e: Event) => {
          e.preventDefault();
          this._cancel();
        }}
      >
        ${
          this._error
            ? html`<sl-alert variant="danger" open style="margin-bottom: 12px;"
                >${this._error}</sl-alert
              >`
            : nothing
        }

        <div class="due-line">
          <span class="label">Amount due</span>
          <span class="amount">${money(this.amountDue)}</span>
        </div>

        ${
          this.memberName
            ? html`
                <div class="section-label">
                  ${this.memberName}'s store credit
                </div>
                ${
                  redeemable.length === 0
                    ? html`<div class="empty">
                        No usable store credit on this member.
                      </div>`
                    : html`
                        <div class="notes">
                          ${redeemable.map(
                            (n) => html`
                              <button
                                class="note"
                                @click=${() => this._select(n)}
                              >
                                <span>
                                  <span class="code">${n.code}</span>
                                  <div class="meta">
                                    ${n.source}${
                                      n.expiryUtc
                                        ? ` · expires ${new Date(n.expiryUtc).toLocaleDateString()}`
                                        : ''
                                    }
                                  </div>
                                </span>
                                <span class="bal">${money(n.remaining)}</span>
                              </button>
                            `
                          )}
                        </div>
                      `
                }
                <div class="divider">or enter a code</div>
              `
            : nothing
        }

        <div class="code-row">
          <sl-input
            ${ref(this._inputRef)}
            label="Credit note code or scan"
            placeholder="Type the code or scan the QR"
            clearable
            @keydown=${(e: KeyboardEvent) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void this._applyCode();
              }
            }}
          ></sl-input>
          <sl-button
            variant="primary"
            ?loading=${this._lookupBusy}
            ?disabled=${this._lookupBusy}
            @click=${this._applyCode}
          >
            Apply
          </sl-button>
        </div>

        <div slot="footer" class="footer-buttons">
          <sl-button variant="text" @click=${this._cancel}>Cancel</sl-button>
        </div>
      </sl-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'credit-note-picker': CreditNotePicker;
  }
}
